import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce, useDocumentTitle } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import { MdAddCircleOutline, MdFileUpload } from 'react-icons/md';
import { useSetAtom } from 'jotai';
import { useAuthHttp } from '@hooks';
import { useAuth } from '@context';
import { UserRoleService } from '@services';
import { InventorySKUListing } from '@views/StockBySKU/components/types';
import {
  ARCHIVE_SKU_URL,
  DEBOUNCE_TIME_IN_MS,
  GET_SKU_URL,
  LIST_PRODUCTS_URL,
  LIST_SKUS_URL,
  UPDATE_PRODUCT_DETAILS_URL,
  UPDATE_SKU_URL,
} from '@constants';
import { SelectedSKU } from '@forms/SKUForm/types';
import { CloneScope } from '@forms/SKUForm/clone';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import { FloxSpinner } from '../../tailwindComponents/FloxSpinner';
import { PendingImportsBanner } from '../MasterDataImportReview/PendingImportsBanner';
import { getLastItemSort, updateLastItemSorts } from '../../utils/pagination';
import { isUploadSKUsModalOpenAtom } from '../../store/jotai';
import { SkuMenuOption } from './components/SkuActionsMenu';
import { SkuArchiveModal } from './components/SkuArchiveModal';
import { ProductArchiveModal } from './components/ProductArchiveModal';
import { ProductCardGrid } from './components/ProductCardGrid';
import { SkuFacetRail } from './components/SkuFacetRail';
import { SkuInfoModal } from './components/SkuInfoModal';
import { SkuTable } from './components/SkuTable';
import { SkuToolbar, SkuViewMode } from './components/SkuToolbar';
import { SkuUploadModal } from './components/SkuUploadModal';
import { dedupeSkusById } from './components/skuHelpers';
import { SkuActiveFilters } from './components/SkuActiveFilters';
import { ProductTable } from './components/ProductTable';
import { ProductHierarchyToggle } from './components/ProductHierarchyToggle';
import { ActivePanelEdit } from './components/panelEdit';
import {
  buildPalletPanelUpdate,
  buildPalletRemovalUpdate,
  buildProductDetailsRequest,
  buildSkuPanelUpdate,
} from './components/panelSave';
import {
  ProductGroupListing,
  applyFacetsToGroups,
  dedupeGroupsByKey,
  productGroupKey,
  productGroupName,
  representativeSkuOf,
} from './components/productGroups';
import {
  SkuFacetState,
  applyFacets,
  buildHierarchyTree,
  deriveBrandOptions,
  emptySkuFacetState,
  hasActiveFacets,
  pruneFacetState,
  selectAllProducts,
  selectCategory,
  selectSubCategory,
  selectUnassigned,
  toggleBrand,
} from './components/skuFacets';

type FetchMode = 'reset' | 'more';

const pages = [
  { name: 'Home', href: '/', current: false },
  { name: 'Products', href: '/sku-master', current: true },
];

export function SKUMaster() {
  useDocumentTitle('FLOX - Products');
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const isWarehouseUser = isWarehouseClient(user);
  const setUploadSKUsModalOpen = useSetAtom(isUploadSKUsModalOpenAtom);

  const [isLoadingSkus, setIsLoadingSkus] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [skus, setSkus] = useState<InventorySKUListing[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [productGroups, setProductGroups] = useState<ProductGroupListing[]>([]);
  const [productTotalCount, setProductTotalCount] = useState(0);
  const [productLastItemSorts, setProductLastItemSorts] = useState<object[]>(
    []
  );
  const latestRequestId = useRef(0);
  const hasFetchedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME_IN_MS);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [facets, setFacets] = useState<SkuFacetState>(emptySkuFacetState);
  const [isRailVisible, setIsRailVisible] = useState(false);
  const [view, setView] = useState<SkuViewMode>('products');
  const [lastItemSorts, setLastItemSorts] = useState<object[]>([]);
  const [openSkuModal, setOpenSkuModal] = useState(false);
  const [selectedSku, setSelectedSku] = useState<InventorySKUListing | null>(
    null
  );
  const [isArchiveSkuModalOpen, setIsArchiveSkuModalOpen] = useState(false);
  const [groupPendingArchive, setGroupPendingArchive] =
    useState<ProductGroupListing | null>(null);
  const [productOrderBy, setProductOrderBy] = useState<string | null>(null);
  const [isProductOrderAscending, setIsProductOrderAscending] = useState(true);
  const [isProductListExhausted, setIsProductListExhausted] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [importRefreshCounter, setImportRefreshCounter] = useState(0);

  const isProductsView = view !== 'list';

  const sharedListFilters = () => ({
    searchQuery: debouncedSearchQuery,
    isIncludingArchived: !!includeArchived,
  });

  const fetchSkus = async (mode: FetchMode = 'reset') => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    const isGoingNext = mode === 'more' ? true : undefined;
    const setLoading = mode === 'more' ? setIsLoadingMore : setIsLoadingSkus;
    setLoading(true);
    try {
      const { data } = await authHttp.post(LIST_SKUS_URL, {
        ...sharedListFilters(),
        pagination: {
          itemsPerPage: 100,
          lastItemSort: getLastItemSort(isGoingNext, lastItemSorts),
        },
      });

      if (requestId !== latestRequestId.current) return;

      if (mode === 'more') {
        setSkus((previous) => dedupeSkusById([...previous, ...data.skus]));
      } else {
        setSkus(data.skus);
        setFacets((previous) => pruneFacetState(previous, data.skus));
      }
      setTotalCount(
        (previous) =>
          data.totalCount ?? (mode === 'more' ? previous : data.skus.length)
      );
      updateLastItemSorts(isGoingNext, setLastItemSorts, data.lastItemSort);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductGroups = async (mode: FetchMode = 'reset') => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    const isGoingNext = mode === 'more' ? true : undefined;
    const setLoading = mode === 'more' ? setIsLoadingMore : setIsLoadingSkus;
    setLoading(true);
    try {
      const { data } = await authHttp.post(LIST_PRODUCTS_URL, {
        ...sharedListFilters(),
        orderBy: productOrderBy ?? undefined,
        isOrderAscending: isProductOrderAscending,
        pagination: {
          itemsPerPage: 50,
          lastItemSort: getLastItemSort(isGoingNext, productLastItemSorts),
        },
      });

      if (requestId !== latestRequestId.current) return;

      if (mode === 'more') {
        const merged = dedupeGroupsByKey([...productGroups, ...data.products]);
        if (merged.length === productGroups.length) {
          setIsProductListExhausted(true);
        }
        setProductGroups(merged);
      } else {
        setIsProductListExhausted(false);
        setProductGroups(data.products);
        setFacets((previous) =>
          pruneFacetState(
            previous,
            data.products.flatMap((group: ProductGroupListing) => group.skus)
          )
        );
      }
      setProductTotalCount(
        (previous) =>
          data.totalCount ?? (mode === 'more' ? previous : data.products.length)
      );
      updateLastItemSorts(
        isGoingNext,
        setProductLastItemSorts,
        data.lastItemSort
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveView = (mode: FetchMode = 'reset') => {
    if (isProductsView) {
      if (mode === 'reset') {
        setSkus([]);
        setLastItemSorts([]);
      }
      return fetchProductGroups(mode);
    }
    if (mode === 'reset') {
      setProductGroups([]);
      setProductLastItemSorts([]);
    }
    return fetchSkus(mode);
  };

  const archiveSku = async (sku: InventorySKUListing, isArchiving: boolean) => {
    setActionError(null);
    try {
      const { data } = await authHttp.post(ARCHIVE_SKU_URL, {
        skuId: sku.inventorySkuId,
        isArchiving,
      });

      if (!data.sku) return;
      const removeFromList = isArchiving && !includeArchived;
      setSkus((previous) =>
        removeFromList
          ? previous.filter((s) => s.inventorySkuId !== sku.inventorySkuId)
          : previous.map((s) =>
              s.inventorySkuId === sku.inventorySkuId
                ? { ...s, isArchived: isArchiving }
                : s
            )
      );
      if (removeFromList)
        setTotalCount((previous) => Math.max(0, previous - 1));

      const groupsAfterArchive = productGroups
        .map((group) => {
          const skusAfterArchive = removeFromList
            ? group.skus.filter((s) => s.inventorySkuId !== sku.inventorySkuId)
            : group.skus.map((s) =>
                s.inventorySkuId === sku.inventorySkuId
                  ? { ...s, isArchived: isArchiving }
                  : s
              );
          const skuCount =
            group.skuCount - (group.skus.length - skusAfterArchive.length);
          return { ...group, skus: skusAfterArchive, skuCount };
        })
        .filter((group) => group.skus.length > 0);
      if (groupsAfterArchive.length < productGroups.length)
        setProductTotalCount((count) => Math.max(0, count - 1));
      setProductGroups(groupsAfterArchive);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      const serverMessage = (
        error as {
          response?: { data?: { errors?: { errorMessage?: string }[] } };
        }
      )?.response?.data?.errors?.[0]?.errorMessage;
      setActionError(
        serverMessage ??
          `Could not ${isArchiving ? 'archive' : 'unarchive'} ${
            sku.name
          }. Refresh and try again.`
      );
    }
  };

  const loadedGroupForKey = (groupKey: string): ProductGroupListing | null => {
    const loadedByKey = productGroups.find(
      (candidate) => productGroupKey(candidate) === groupKey
    );
    if (loadedByKey) return loadedByKey;

    const visibleGroup = applyFacetsToGroups(productGroups, facets).find(
      (candidate) => productGroupKey(candidate) === groupKey
    );
    if (!visibleGroup) return null;

    const visibleSkuIds = new Set(
      visibleGroup.skus.map((sku) => sku.inventorySkuId)
    );
    return (
      productGroups.find((candidate) =>
        candidate.skus.some((sku) => visibleSkuIds.has(sku.inventorySkuId))
      ) ?? visibleGroup
    );
  };

  const fetchFullSku = async (inventorySkuId: string) => {
    const { data } = await authHttp.get<SelectedSKU>(GET_SKU_URL, {
      params: { skuId: inventorySkuId },
    });
    return data?.sku ? data : null;
  };

  const postSkuUpdate = (payload: object) =>
    authHttpFormData.post(UPDATE_SKU_URL, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

  const reportSaveFailure = (error: unknown, fallbackMessage: string) => {
    // eslint-disable-next-line no-console
    console.error(error);
    const serverMessage = (
      error as {
        response?: { data?: { errors?: { errorMessage?: string }[] } };
      }
    )?.response?.data?.errors?.[0]?.errorMessage;
    setActionError(serverMessage ?? fallbackMessage);
  };

  const savePanelEdit = async (edit: ActivePanelEdit): Promise<boolean> => {
    setActionError(null);

    try {
      if (edit.kind === 'product') {
        await authHttpFormData.post(
          UPDATE_PRODUCT_DETAILS_URL,
          buildProductDetailsRequest(edit.draft),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        const selected = await fetchFullSku(edit.inventorySkuId);
        if (!selected) return false;
        await postSkuUpdate(
          edit.kind === 'sku'
            ? buildSkuPanelUpdate(selected, edit.draft)
            : buildPalletPanelUpdate(selected, edit.palletIndex, edit.draft)
        );
      }
      await fetchProductGroups('reset');
      return true;
    } catch (error) {
      reportSaveFailure(
        error,
        'Could not save your changes. Refresh and try again.'
      );
      return false;
    }
  };

  const removePallet = async (
    sku: InventorySKUListing,
    palletIndex: number
  ): Promise<boolean> => {
    setActionError(null);
    try {
      const selected = await fetchFullSku(sku.inventorySkuId);
      if (!selected) return false;
      await postSkuUpdate(buildPalletRemovalUpdate(selected, palletIndex));
      await fetchProductGroups('reset');
      return true;
    } catch (error) {
      reportSaveFailure(
        error,
        `Could not remove the pallet from ${sku.name}. Refresh and try again.`
      );
      return false;
    }
  };

  const archiveProduct = async (confirmedGroup: ProductGroupListing) => {
    setActionError(null);
    const group =
      loadedGroupForKey(productGroupKey(confirmedGroup)) ?? confirmedGroup;
    try {
      await Promise.all(
        group.skus
          .filter((sku) => !sku.isArchived)
          .map((sku) =>
            authHttp.post(ARCHIVE_SKU_URL, {
              skuId: sku.inventorySkuId,
              isArchiving: true,
            })
          )
      );
      await fetchProductGroups('reset');
    } catch (error) {
      reportSaveFailure(
        error,
        `Could not archive ${productGroupName(group)}. Refresh and try again.`
      );
    }
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 1 && debouncedSearchQuery.length < 4)
      return;
    fetchActiveView('reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      return;
    }
    fetchActiveView('reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  const handleSortProducts = (column: string) => {
    if (productOrderBy !== column) {
      setProductOrderBy(column);
      setIsProductOrderAscending(true);
    } else if (isProductOrderAscending) {
      setIsProductOrderAscending(false);
    } else {
      setProductOrderBy(null);
    }
  };

  const hasSortEffectRunRef = useRef(false);
  useEffect(() => {
    if (!hasSortEffectRunRef.current) {
      hasSortEffectRunRef.current = true;
      return;
    }
    setProductLastItemSorts([]);
    fetchProductGroups('reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOrderBy, isProductOrderAscending]);

  const hasViewEffectRunRef = useRef(false);
  useEffect(() => {
    if (!hasViewEffectRunRef.current) {
      hasViewEffectRunRef.current = true;
      return;
    }
    if (isProductsView && productGroups.length === 0) {
      fetchProductGroups('reset');
    } else if (!isProductsView && skus.length === 0) {
      fetchSkus('reset');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const facetSourceSkus = useMemo(
    () => (isProductsView ? productGroups.map(representativeSkuOf) : skus),
    [isProductsView, productGroups, skus]
  );
  const visibleSkus = useMemo(() => applyFacets(skus, facets), [skus, facets]);
  const visibleGroups = useMemo(
    () => applyFacetsToGroups(productGroups, facets),
    [productGroups, facets]
  );
  const hierarchyTree = useMemo(
    () => buildHierarchyTree(facetSourceSkus, facets),
    [facetSourceSkus, facets]
  );
  const brandOptions = useMemo(
    () => deriveBrandOptions(facetSourceSkus, facets),
    [facetSourceSkus, facets]
  );
  const facetsActive = hasActiveFacets(facets);
  const hasMore = isProductsView
    ? productGroups.length < productTotalCount && !isProductListExhausted
    : skus.length < totalCount;

  const handleOpenSkuModal = (sku: InventorySKUListing) => {
    setSelectedSku(sku);
    setOpenSkuModal(true);
  };

  const handleCloseSkuModal = () => {
    setOpenSkuModal(false);
    setSelectedSku(null);
  };

  const handleOpenArchiveSkuModal = (sku: InventorySKUListing) => {
    setSelectedSku(sku);
    setIsArchiveSkuModalOpen(true);
  };

  const handleCloseArchiveSkuModal = () => {
    setIsArchiveSkuModalOpen(false);
    setSelectedSku(null);
  };

  const handleChangeSearchQuery = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClearFilters = () => setFacets(emptySkuFacetState());

  const handleCloneFromModal = (sku: InventorySKUListing) => {
    handleCloseSkuModal();
    navigate(`/sku/create?cloneFrom=${sku.inventorySkuId}`);
  };

  const handleUpdateFromModal = (sku: InventorySKUListing) => {
    handleCloseSkuModal();
    navigate(`/sku/update/${sku.inventorySkuId}`);
  };

  const handleArchiveFromModal = (sku: InventorySKUListing) => {
    setOpenSkuModal(false);
    handleOpenArchiveSkuModal(sku);
  };

  const buildMenuOptions = (sku: InventorySKUListing): SkuMenuOption[] => [
    {
      label: 'Edit',
      action: () => navigate(`/sku/update/${sku.inventorySkuId}`),
    },
    {
      label: 'Clone',
      action: () => navigate(`/sku/create?cloneFrom=${sku.inventorySkuId}`),
    },
  ];

  const handleAddPackSize = (group: ProductGroupListing) => {
    const sourceSku = group.skus[0];
    if (!sourceSku) return;
    navigate(`/sku/create?productFrom=${sourceSku.inventorySkuId}`);
  };

  const cloneGroup = (group: ProductGroupListing, scope: CloneScope) => {
    const sourceSku = group.skus[0];
    if (!sourceSku) return;
    navigate(
      `/sku/create?cloneFrom=${sourceSku.inventorySkuId}&cloneScope=${scope}`
    );
  };

  const handleOpenProductTile = (group: ProductGroupListing) => {
    const representative = group.skus[0];
    if (representative) handleOpenSkuModal(representative);
  };

  const buildProductMenuOptions = (
    group: ProductGroupListing
  ): SkuMenuOption[] => [
    { label: 'Add pack size', action: () => handleAddPackSize(group) },
    { label: 'Clone', action: () => cloneGroup(group, 'product') },
    { label: 'Archive', action: () => setGroupPendingArchive(group) },
  ];

  const productArchivedAlongside = (sku: InventorySKUListing | null) => {
    if (!sku || sku.isArchived) return null;
    const group = productGroups.find((candidate) =>
      candidate.skus.some((s) => s.inventorySkuId === sku.inventorySkuId)
    );
    if (group) {
      const activeVariants = group.skus.filter((s) => !s.isArchived);
      const isLastActiveVariant =
        activeVariants.length === 1 &&
        activeVariants[0].inventorySkuId === sku.inventorySkuId;
      if (!isLastActiveVariant) return null;
      return group.product?.name || group.skus[0]?.name || null;
    }

    const productId = sku.productDetails?.productId;
    if (!productId) return null;
    const loadedSiblings = skus.filter(
      (s) => s.productDetails?.productId === productId
    );
    if (!loadedSiblings.length) return null;
    const activeSiblings = loadedSiblings.filter((s) => !s.isArchived);
    const isLastLoadedActiveSibling =
      activeSiblings.length === 1 &&
      activeSiblings[0].inventorySkuId === sku.inventorySkuId;
    if (!isLastLoadedActiveSibling) return null;
    return sku.productDetails?.name || sku.name || null;
  };

  const resolveResultsSummary = () => {
    if (isProductsView) {
      if (facetsActive)
        return `Showing ${visibleGroups.length} of ${productGroups.length} loaded products (${productTotalCount} total)`;
      return `Showing ${productGroups.length} of ${productTotalCount} products`;
    }
    if (facetsActive)
      return `Showing ${visibleSkus.length} of ${skus.length} loaded SKUs (${totalCount} total)`;
    return `Showing ${skus.length} of ${totalCount} SKUs`;
  };
  const resultsSummary = resolveResultsSummary();

  return (
    <>
      <PageHeader
        header='Products'
        pages={pages}
        headerAdornment={
          <ProductHierarchyToggle view={view} onViewChange={setView} />
        }
      >
        <div className='flex items-center gap-2'>
          {isWarehouseUser && (
            <Button
              label='Add product'
              intent='primary'
              startIcon={<MdAddCircleOutline />}
              onClick={() => navigate('/sku/create')}
            />
          )}
          {isWarehouseUser && (
            <Button
              intent='secondary'
              label='Upload products'
              onClick={() => setUploadSKUsModalOpen(true)}
              startIcon={<MdFileUpload />}
            />
          )}
        </div>
      </PageHeader>
      <PendingImportsBanner
        entityType='InventorySku'
        refreshKey={importRefreshCounter}
      />
      <main className='flex flex-col gap-4 px-8 py-6 font-inter'>
        <SkuToolbar
          searchQuery={searchQuery}
          onSearchQueryChange={handleChangeSearchQuery}
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
          isRailVisible={isRailVisible}
          onToggleRail={() => setIsRailVisible((visible) => !visible)}
          view={view}
          onViewChange={setView}
        />
        <div className='flex items-start gap-6'>
          {isRailVisible && (
            <SkuFacetRail
              tree={hierarchyTree}
              facets={facets}
              onSelectAll={() => setFacets(selectAllProducts(facets))}
              onSelectUnassigned={() => setFacets(selectUnassigned(facets))}
              onSelectCategory={(category) =>
                setFacets(selectCategory(facets, category))
              }
              onSelectSubCategory={(category, subCategory) =>
                setFacets(selectSubCategory(facets, category, subCategory))
              }
              brandOptions={brandOptions}
              onToggleBrand={(value) => setFacets(toggleBrand(facets, value))}
              countNoun={view === 'products' ? 'products' : 'SKUs'}
            />
          )}
          <div className='flex min-w-0 flex-1 flex-col gap-3'>
            {actionError && (
              <div className='flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700'>
                <span>{actionError}</span>
                <button
                  type='button'
                  aria-label='Dismiss error'
                  className='shrink-0 font-medium underline'
                  onClick={() => setActionError(null)}
                >
                  Dismiss
                </button>
              </div>
            )}
            <SkuActiveFilters
              facets={facets}
              onClearHierarchy={() =>
                setFacets(
                  facets.subCategory && facets.category
                    ? selectCategory(facets, facets.category)
                    : selectAllProducts(facets)
                )
              }
              onRemoveBrand={(value) => setFacets(toggleBrand(facets, value))}
              onClearAll={handleClearFilters}
            />
            {view === 'products' && (
              <ProductTable
                groups={visibleGroups}
                isLoading={isLoadingSkus}
                isWarehouseUser={isWarehouseUser}
                onArchiveSku={handleOpenArchiveSkuModal}
                onArchiveProduct={setGroupPendingArchive}
                hasActiveFilters={facetsActive}
                onClearFilters={handleClearFilters}
                canManageSkus={isWarehouseUser}
                onUploadClick={() => setUploadSKUsModalOpen(true)}
                onAddClick={() => navigate('/sku/create')}
                onAddPackSize={handleAddPackSize}
                onCloneProduct={(group) => cloneGroup(group, 'product')}
                onCloneProductSkus={(group) => cloneGroup(group, 'skus')}
                onCloneHierarchy={(group) => cloneGroup(group, 'hierarchy')}
                onSavePanelEdit={savePanelEdit}
                onRemovePallet={removePallet}
                orderBy={productOrderBy}
                isOrderAscending={isProductOrderAscending}
                onSortColumn={handleSortProducts}
              />
            )}
            {view === 'list' && (
              <SkuTable
                skus={visibleSkus}
                isLoading={isLoadingSkus}
                isWarehouseUser={isWarehouseUser}
                onOpenSku={handleOpenSkuModal}
                buildMenuOptions={buildMenuOptions}
                onArchiveSku={handleOpenArchiveSkuModal}
                hasActiveFilters={facetsActive}
                onClearFilters={handleClearFilters}
                canManageSkus={isWarehouseUser}
                onUploadClick={() => setUploadSKUsModalOpen(true)}
                onAddClick={() => navigate('/sku/create')}
              />
            )}
            {view === 'grid' &&
              (isLoadingSkus ? (
                <div className='flex justify-center py-12'>
                  <FloxSpinner size='lg' />
                </div>
              ) : (
                <ProductCardGrid
                  groups={visibleGroups}
                  isWarehouseUser={isWarehouseUser}
                  onOpenProduct={handleOpenProductTile}
                  buildMenuOptions={buildProductMenuOptions}
                />
              ))}
            {!isLoadingSkus &&
              (isProductsView ? productGroups.length > 0 : skus.length > 0) && (
                <div className='flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500'>
                  <span>{resultsSummary}</span>
                  <div className='flex items-center gap-3'>
                    {facetsActive && hasMore && (
                      <span className='text-xs text-gray-400'>
                        Filters apply to loaded{' '}
                        {isProductsView ? 'products' : 'SKUs'} only — load more
                        to include the rest.
                      </span>
                    )}
                    {hasMore && (
                      <Button
                        intent='secondary'
                        size='sm'
                        label={
                          isLoadingMore
                            ? 'Loading...'
                            : `Load ${isProductsView ? '50' : '100'} more`
                        }
                        disabled={isLoadingMore}
                        onClick={() =>
                          isProductsView
                            ? fetchProductGroups('more')
                            : fetchSkus('more')
                        }
                      />
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </main>
      <SkuInfoModal
        sku={selectedSku}
        isOpen={openSkuModal}
        onClose={handleCloseSkuModal}
        isWarehouseUser={isWarehouseUser}
        onClone={handleCloneFromModal}
        onUpdate={handleUpdateFromModal}
        onArchive={handleArchiveFromModal}
      />
      <ProductArchiveModal
        group={groupPendingArchive}
        isOpen={groupPendingArchive !== null}
        onClose={() => setGroupPendingArchive(null)}
        onConfirm={archiveProduct}
      />
      <SkuArchiveModal
        sku={selectedSku}
        isOpen={isArchiveSkuModalOpen}
        onClose={handleCloseArchiveSkuModal}
        onConfirm={archiveSku}
        archivesProductName={productArchivedAlongside(selectedSku)}
      />
      <SkuUploadModal
        onUploaded={() => {
          fetchActiveView('reset');
          setImportRefreshCounter((count) => count + 1);
        }}
      />
    </>
  );
}
